/**
 * Helper functions for BCRS (Beverage Container Return Scheme) calculations
 *
 * Note: Due to backend inconsistency, BCRS data is stored in different locations:
 * - Cart/PaymentData format: item.bcrs
 * - OrderDetail format: item.details.bcrs
 * The helper functions handle both formats automatically using fallback logic.
 */

interface BcrsItem {
	bcrs?: number;
	details?: {
		bcrs?: number;
	};
	quantity: number;
	food_supplier_set_items?: BcrsSetItem[];
}

interface BcrsSetItem {
	bcrs?: number;
	details?: {
		bcrs?: number;
	};
	quantity: number;
}

/**
 * Calculate total BCRS amount for items (including set items)
 * @param items Array of items with bcrs information (supports both item.bcrs and item.details.bcrs)
 * @returns Total BCRS count
 */
export const calculateTotalBcrs = (items: BcrsItem[]): number => {
	return items.reduce((total, item) => {
		// Main item bcrs - fallback from item.bcrs to item.details.bcrs due to backend inconsistency
		let itemBcrs = (item?.bcrs ?? item.details?.bcrs ?? 0) * item.quantity;

		// Add set items bcrs
		if (item.food_supplier_set_items) {
			const setItemsBcrs = item.food_supplier_set_items.reduce((setTotal, setItem) => {
				// Set item bcrs - same fallback logic for consistency
				return setTotal + (setItem.bcrs ?? setItem.details?.bcrs ?? 0) * setItem.quantity;
			}, 0);

			itemBcrs += setItemsBcrs * item.quantity; // Multiply by main item quantity
		}

		return total + itemBcrs;
	}, 0);
};

/**
 * Calculate total BCRS deposit amount
 * @param items Array of items with bcrs information (supports both item.bcrs and item.details.bcrs)
 * @param depositRate Deposit rate per BCRS unit (in cents)
 * @returns Total deposit amount in cents
 */
export const calculateTotalBcrsDeposit = (items: BcrsItem[], depositRate = 0): number => {
	const totalBcrs = calculateTotalBcrs(items);

	return totalBcrs * depositRate;
};
